import {Mental} from "../../Mental";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {IMentalGameData, IMentalInitData} from "../../server/MentalData";
import {TweenLite} from "gsap";
import {MentalGameMode} from "../../server/MentalGameMode";
import {SoundManager} from "../../sound/SoundManager";
import {MentalSoundConfig} from "../../MentalSoundConfig";
import {MentalReelSet} from "../../reel/MentalReelSet";
import {MentalHelper} from "../../utils/MentalHelper";

export class MentalFsOutro extends PIXI.Container implements StageLayer {
    private _button: ContinueButton;
    private _buttonCallBack: Function;
    private _outroButtonContainer: PIXI.Container;
    private _outroSpine: PIXI.spine.Spine;
    private _overlay: PIXI.Graphics;
    private _textContainer: PIXI.Container;
    private _youWonText: PIXI.Text;
    private _amountText: PIXI.Text;
    private _spinsPlayedText: PIXI.Text;
    private _spinsPlayedNum: PIXI.Text;
    private _gameData: IMentalInitData | IMentalGameData;
    static FS_OUTRO_TEXT: PIXI.TextStyle = new PIXI.TextStyle({
        fontFamily: "VeteranTypewriter",
        fill: "#ffe18f",
        fontSize: "45px"
    });
    static FS_OUTRO_NUM: PIXI.TextStyle = new PIXI.TextStyle({
        fontFamily: "VeteranTypewriter",
        fill: "#ffe18f",
        fontSize: "90px"
    });

    constructor() {
        super();
        this.initAnimations();
    }

    private initContinueButton(): PIXI.Container {
        this._outroButtonContainer?.removeChildren();
        const outroContainer: PIXI.Container = new PIXI.Container();

        this._button = this.createButton();
        outroContainer.alpha = 0;
        outroContainer.visible = false;
        outroContainer.addChild(this._button);
        return outroContainer;
    }

    private createButton(): ContinueButton {
        let continueBtn: ContinueButton = this.addChild(new ContinueButton());
        continueBtn.x -= continueBtn.width / 2;
        continueBtn.y -= continueBtn.height / 2;
        continueBtn.addClickCallback(() => this.onButtonClick());
        continueBtn.enable(true);

        return continueBtn;
    }

    private resetOutroAssets(dur: number): void {
        this._spinsPlayedNum.text = "";
        this._amountText.text = "";
        TweenLite.to([this._overlay, this._outroSpine, this._textContainer, this._outroButtonContainer], {
            duration: dur,
            alpha: 0,
            onComplete: () => {
                this._overlay.visible = false;
                this._outroSpine.state.setEmptyAnimation(0, 0);
                this._outroSpine.state.clearTracks();
                this._outroSpine.visible = false;
                this._textContainer.visible = false;
                this._outroButtonContainer.visible = false;
            }
        });
    }

    private getCurrentMode(): string {
        return this._gameData.mode;
    }

    private getAnimName(): string {
        let animName: string,
            currMode: string = this.getCurrentMode();

        if (currMode === MentalGameMode.COOL_FREESPIN) {
            animName = "cool_animation";
        } else if (currMode === MentalGameMode.SUPER_FREESPIN) {
            animName = "super_cool_animation";
        } else {
            animName = "standard_animation";
        }

        return animName;
    }

    private showOutroContent(): void {
        this._amountText.text = (this._gameData.accumulatedRoundWin.toFixed(2)).toString();
        this._spinsPlayedNum.text = this._gameData.numberOfFreespinsPlayed.toString();

        this._overlay.visible = true;
        this._outroSpine.visible = true;
        this._outroSpine.alpha = 0;
        //Mental.keypadPlugin.setWin(this._gameData.accumulatedRoundWin, true, MentalHelper.isWinBelowStake()); //#399
        TweenLite.to(this._overlay, 0.5, {alpha: 1});
        TweenLite.to(this._outroSpine, 0.01, {
            alpha: 1,
            delay: 0.3,
            onStart: () => {
                // #394
                //if (!(Mental.apiPlugin.gameClientConfiguration.belowStakeWinRestriction && (this._gameData.accumulatedRoundWin <= (this._gameData as IMentalGameData).playedBetValue))) {
                if (!(MentalHelper.isWinBelowStake())) {
                    SoundManager.playSoundEffect(MentalSoundConfig.FS_END);
                }
            },
            onComplete: () => {
                this.playSpineAnim();
                // #138
                SoundManager.stopFSMusic();
            }
        });

        //#271
        MentalReelSet.updateReelSet();
    }

    private playSpineAnim(): void {
        let animName: string = this.getAnimName();

        this._outroSpine.updateTransform();
        this._outroSpine.state.clearTracks();
        this._outroSpine.skeleton.setToSetupPose();

        TweenLite.to(this._outroSpine.state.setAnimation(0, animName, false), {
            onComplete: () => {
                TweenLite.to([this._textContainer, this._outroButtonContainer], 0.1, {alpha: 1}).eventCallback("onStart", () => {
                    this._textContainer.visible = true;
                    this._youWonText.visible = !MentalHelper.isWinBelowStake(); //#399
                    this._outroButtonContainer.visible = true;
                    this._button.visible = true;
                    this._outroSpine.state.setAnimation(0, animName + "_loop", true);
                })
            }
        });
    }

    private initAnimations() {
        this._overlay = MentalFsOutro.createOverlay();
        this._outroSpine = MentalFsOutro.createSpine("fsOutroSpine");

        this._textContainer = this.createTextContainer();
        this._outroButtonContainer = this.initContinueButton();

        this.addChild(this._overlay, this._outroSpine, this._textContainer, this._outroButtonContainer);

        this.onResize();
    }

    private createTextContainer(): PIXI.Container {
        let textContainer: PIXI.Container = new PIXI.Container();
        textContainer.position.set(360);
        textContainer.alpha = 0;

        this._youWonText = new PIXI.Text(Mental.apiPlugin.translations.translate("You won:"), MentalFsOutro.FS_OUTRO_TEXT);
        this._youWonText.anchor.set(0.5);
        this._youWonText.position.y = -70;

        this._amountText = new PIXI.Text("", MentalFsOutro.FS_OUTRO_NUM);
        this._amountText.anchor.set(0.5);

        this._spinsPlayedText = new PIXI.Text(Mental.apiPlugin.translations.translate("Spins Played:"), MentalFsOutro.FS_OUTRO_TEXT);
        this._spinsPlayedText.anchor.set(0.5);
        this._spinsPlayedText.position.y = 80;

        this._spinsPlayedNum = new PIXI.Text("", MentalFsOutro.FS_OUTRO_NUM);
        this._spinsPlayedNum.anchor.set(0.5);
        this._spinsPlayedNum.position.y = 150;

        textContainer.addChild(this._youWonText, this._amountText, this._spinsPlayedText, this._spinsPlayedNum);

        return textContainer;
    }

    private onButtonClick(): void {
        this._button.visible = false;
        Mental.keypadPlugin.show();
        Mental.deadFeatureStickyMultiplier.hideSymbolStickers();
        Mental.stickyScatter.removeStickyScatteronPos();
        this._buttonCallBack();
        this.resetOutroAssets(0.5);

        // #228
        SoundManager.onFsOutroBtnClick();

        // #107
        Mental.fireFrame.exitFS();

        // #131
        Mental.background.flickBackgroundLight(true);

        Mental.model.buyFeatureName = ""; //resetBuyFeatureName
    }

    private static createOverlay(): PIXI.Graphics {
        let mask: PIXI.Graphics = new PIXI.Graphics();
        mask.beginFill(0x000000, 0.75);
        mask.drawRect(-700, -700, 3600, 3600);
        mask.endFill();
        mask.alpha = 0;

        return mask;
    }

    private static createSpine(assetName: string): PIXI.spine.Spine {
        const outroSpine: PIXI.spine.Spine = new PIXI.spine.Spine(Mental.assetLoader.getSpineAsset(assetName));
        outroSpine.visible = false;
        outroSpine.position.set(360);
        return outroSpine;
    }

    public onResize(): void {
        if (NolimitApplication.isLandscape) {
            this._outroButtonContainer.position.set(360, 615);
        } else {
            const centerBottom: number = (NolimitApplication.screenBounds.bottom - 610) * 0.5 + 610;
            this._outroButtonContainer.position.set(360, centerBottom);
        }
    }

    public showOutro(callBackFn: Function): void {
        Mental.keypadPlugin.hide();
        this._buttonCallBack = callBackFn;
        this._gameData = Mental.model.data;

        this.showOutroContent();
    }
}